import { createFileRoute, Link } from "@tanstack/react-router";
import portraitImg from "@/assets/dj-mariage-portrait.jpg";
import heroImg from "@/assets/dj-mariage-reception.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
export const Route = createFileRoute("/a-propos")({
  head: () => ({
    meta: [
      { title: "DJ mariage Vaucluse depuis 20 ans | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("DJ Val, DJ professionnel à Cavaillon. 20 ans d'expérience, 200+ mariages animés en Provence. Parcours, matériel pro et philosophie.")

      },
      { property: "og:title", content: "DJ Mariage Vaucluse depuis 20 ans | DJ Val" },
      { property: "og:description", content: metaDesc("20 ans d'expérience, 200+ mariages, 500+ événements privés, 1000+ locations de matériel en Provence.") },
      { property: "og:url", content: "https://dj-val.fr/a-propos" },
      { property: "og:image", content: `https://dj-val.fr${portraitImg}` },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${portraitImg}` },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/a-propos" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": "https://dj-val.fr/#val",
          name: "DJ Val",
          givenName: "Val",
          jobTitle: "DJ professionnel mariage & événements",
          description:
            "DJ professionnel installé à Cavaillon depuis 20 ans. Plus de 200 mariages, 500+ événements privés et 1000+ locations de matériel en Provence et Vaucluse.",
          image: portraitImg,
          telephone: "+33767908667",
          email: "info.djval@gmail.com",
          worksFor: { ...LOCAL_BUSINESS_SCHEMA },
          address: {
            "@type": "PostalAddress",
            streetAddress: "106 Grand Rue",
            addressLocality: "Cavaillon",
            postalCode: "84300",
            addressRegion: "Vaucluse",
            addressCountry: "FR",
          },
          knowsAbout: [
            "Animation musicale mariage",
            "Mix DJ",
            "Sonorisation événementielle",
            "Éclairage scénique LED",
            "Cérémonie laïque",
            "Soirée d'entreprise",
          ],
          knowsLanguage: ["fr", "en"],
          sameAs: [
            "https://www.facebook.com/djval84",
            "https://www.instagram.com/djval_provence",
            "https://www.google.com/maps/search/?api=1&query=DJ+Val+Cavaillon&query_place_id=ChIJn2-Ifaz3tRIRQGJB4UPeKaw",
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const VALUES = [
  { title: "Écoute", text: "Chaque mariage est unique. Nous construisons votre playlist et le déroulé à partir de vos goûts, pas d'un modèle pré-fait." },
  { title: "Discrétion", text: "Présence élégante, transitions fluides, gestion micro maîtrisée. Le DJ ne se voit pas, il se ressent." },
  { title: "Fiabilité", text: "Double matériel de secours, arrivée 3h avant, repérage technique du lieu. Aucune mauvaise surprise." },
  { title: "Énergie", text: "20 ans à faire danser. Une lecture en temps réel de la piste pour garder vos invités jusqu'au bout de la nuit." },
];

const STATS = [
  { value: "20 ans", label: "d'expérience depuis 20 ans" },
  { value: "200+", label: "mariages animés" },
  { value: "500+", label: "événements privés" },
  { value: "1000+", label: "locations de matériel" },
];

const GEAR = [
  { cat: "Mix & son", items: ["Pioneer DJM-900NXS2 (table de mixage)", "Pioneer CDJ-2000NXS2 (lecteurs)", "Macbook Pro + Serato DJ Pro"] },
  { cat: "Sonorisation", items: ["Enceintes RCF HDL 20-A (line array)", "Subwoofers RCF SUB 8004-AS", "Retours Yamaha DXR12"] },
  { cat: "Micros HF", items: ["Shure ULXD (cérémonie)", "Shure SM58 (filaire de secours)", "Sennheiser EW-D (lavalier)"] },
  { cat: "Éclairage", items: ["Lyres LED Chauvet Rogue R2X", "Pars LED Cameo Flat Pro", "Lasers Laserworld + machine brouillard Antari"] },
];

const PHILOSOPHIE = [
  { step: "01", title: "Rendez-vous découverte", text: "On se rencontre (en visio ou en personne) pour faire connaissance et caler vos envies." },
  { step: "02", title: "Repérage du lieu", text: "Visite du domaine pour valider acoustique, accès, alimentation, contraintes sonores." },
  { step: "03", title: "Trame personnalisée", text: "On construit ensemble le déroulé minute par minute : cocktail, dîner, ouverture de bal, soirée." },
  { step: "04", title: "Playlist co-construite", text: "Must-play, no-go, morceaux signatures. Vous gardez la main, j'apporte l'expertise." },
  { step: "05", title: "Jour J maîtrisé", text: "Arrivée 3h avant, installation propre, test technique. Vous n'avez plus qu'à profiter." },
];

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO immersif */}
      <PageHero
        image={heroImg}
        eyebrow="Le DJ derrière la cabine"
        title={<><span className="italic">DJ Val</span><span className="block">20 ans à faire danser la Provence</span></>}
        subtitle="Installé à Cavaillon depuis 20 ans — 200+ mariages, 500+ événements, 1000+ locations."
        primary={{ label: "Me contacter", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
      />
      <ReviewsProof />

      {/* INTRO */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="sr" data-reveal>
            <p className="eyebrow text-foreground">Mon parcours</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Un DJ professionnel<span className="block italic mt-1">à temps plein</span>
            </h2>

            <span className="gold-rule !w-12 my-8" />
            <div className="space-y-5 text-base text-muted-foreground leading-loose font-light">
              <p>
                Installé à Cavaillon au cœur du Vaucluse depuis 20 ans, je suis DJ professionnel
                à temps plein. Plus de 200 mariages, 500+ événements privés et
                1000+ locations de matériel plus tard, ma passion pour faire vibrer les
                pistes de danse est intacte.
              </p>
              <p>
                Mon approche : prendre le temps de comprendre votre couple, votre ambiance
                rêvée, vos invités. Construire avec vous une soirée fluide, élégante et
                fidèle à votre identité — jamais une animation générique de catalogue.
              </p>
              <p>
                Matériel haut de gamme, repérage systématique du lieu, double secours,
                assurance RC pro Hiscox, déclaration SACEM à jour. Le confort de la sérénité
                totale, du premier rendez-vous au dernier morceau.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contactez-nous"
                className="bg-[var(--gold)] text-foreground px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
              >
                Me contacter
              </Link>
              <a
                href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "a_propos" })}
                className="border border-foreground/10 text-foreground px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-foreground transition-colors"
              >
                07 67 90 86 67
              </a>
            </div>
          </div>
          <div className="sr" data-reveal style={{ transitionDelay: "120ms" }}>
            <div className="relative aspect-[4/5] overflow-hidden border border-foreground/10">
              <img loading="lazy" decoding="async"
                src={portraitImg}
                alt="DJ Val, DJ professionnel mariage installé à Cavaillon"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 px-6 border-y border-foreground/10 bg-card">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <div key={s.label} className="sr" data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-4xl sm:text-5xl">{s.value}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-3 leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MATERIEL */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">Matériel professionnel</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Un parc à <span className="italic">30 000 €</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="text-sm text-muted-foreground leading-loose font-light max-w-2xl mx-auto mt-6">
            Marques pro reconnues, doublées en secours, entretenues et calibrées avant chaque prestation.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {GEAR.map((g, i) => (
            <div key={g.cat} className="border border-foreground/10 p-6 sr" data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
              <p className="font-serif-display italic text-foreground text-xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-xl text-foreground mt-3">{g.cat}</h3>
              <span className="gold-rule !w-8 my-4" />
              <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed font-light">
                {g.items.map((it) => (
                  <li key={it} className="flex gap-2"><span className="text-[var(--gold)]">·</span>{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* PHILOSOPHIE / METHODE */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 sr" data-reveal>
            <p className="eyebrow text-foreground">Ma méthode</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              De la première rencontre au <span className="italic">dernier morceau</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="space-y-px bg-foreground/10 border border-foreground/10">
            {PHILOSOPHIE.map((p, i) => (
              <div key={p.title} className="bg-background p-8 grid md:grid-cols-[80px_1fr] gap-6 sr" data-reveal style={{ transitionDelay: `${i * 50}ms` }}>
                <p className="font-serif-display italic text-[var(--gold)] text-3xl">{p.step}</p>
                <div>
                  <h3 className="font-serif-display text-xl text-foreground">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light mt-2">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 sr" data-reveal>
            <p className="eyebrow text-foreground">Mes valeurs</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">Quatre engagements</h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <div key={v.title} className="border border-foreground/10 p-8 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
                <p className="font-serif-display italic text-foreground text-xl">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif-display text-2xl text-foreground mt-3">{v.title}</h3>
                <span className="gold-rule !w-8 my-4" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / certifications */}
      <section className="py-20 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Garanties professionnelles</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-6 text-sm">
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">SACEM</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">Déclaration à jour pour diffusion légale de musique</p>
            </div>
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">RC Pro</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">Assurance Hiscox responsabilité civile professionnelle</p>
            </div>
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">SIRET</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">Entreprise déclarée, facture conforme remise</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            On en parle <span className="italic text-[var(--gold)]">autour d'un café</span> ?
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Discuter de votre projet
          </Link>
        </div>
      </section>
    </div>
  );
}
